/**
 * Offline outbox for check-ins.
 *
 * A check-in must never be lost. Every check-in is written to IndexedDB first
 * and only removed once Postgres has accepted it, so closing the app, losing
 * signal mid-request or force-quitting all end the same way: the check-in is
 * still queued and goes up on the next opportunity.
 *
 * Why not Workbox Background Sync? A queued raw request carries the access
 * token it was created with, and that token expires in an hour — a check-in
 * made in a tunnel at 9pm would replay with a dead JWT and be dropped. Holding
 * the *data* instead of the *request* lets the replay use a fresh session. It
 * also works on iOS, which has no Background Sync at all.
 */
import { supabase, PROOF_PHOTOS_BUCKET } from "./supabase";
import { errorMessage } from "./errors";

const DB_NAME = "lock-in";
const DB_VERSION = 1;
const STORE = "pending-check-ins";

export interface PendingCheckIn {
  id: string;              // the check_ins.id we will insert — makes replay idempotent
  goal_id: string;
  owner_id: string;
  goal_title: string;      // kept for the UI so pending rows render without a join
  note: string | null;
  photo: Blob | null;
  checked_in_at: string;
  created_at: number;
  attempts: number;
  last_error: string | null;
  /** Set when the failure can never resolve itself (e.g. the goal was deleted). */
  permanent: boolean;
}

// ---------------------------------------------------------------------------
// IndexedDB plumbing. Raw, because a wrapper library would be more code than
// this and the surface we need is four calls wide.
// ---------------------------------------------------------------------------

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = run(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }),
  );
}

// ---------------------------------------------------------------------------
// Change notification, so the UI can show "1 waiting to sync" without polling.
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeToOutbox(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  for (const listener of listeners) listener();
}

// ---------------------------------------------------------------------------
// Queue operations
// ---------------------------------------------------------------------------

export async function enqueue(entry: Omit<PendingCheckIn, "attempts" | "last_error" | "permanent" | "created_at">): Promise<void> {
  await tx("readwrite", (store) =>
    store.put({
      ...entry,
      created_at: Date.now(),
      attempts: 0,
      last_error: null,
      permanent: false,
    } satisfies PendingCheckIn),
  );
  notify();
}

export async function listPending(): Promise<PendingCheckIn[]> {
  const rows = await tx<PendingCheckIn[]>("readonly", (store) => store.getAll() as IDBRequest<PendingCheckIn[]>);
  return rows.sort((a, b) => a.created_at - b.created_at);
}

export async function discardPending(id: string): Promise<void> {
  await tx("readwrite", (store) => store.delete(id) as unknown as IDBRequest<undefined>);
  notify();
}

async function update(entry: PendingCheckIn): Promise<void> {
  await tx("readwrite", (store) => store.put(entry));
  notify();
}

// ---------------------------------------------------------------------------
// Flushing
// ---------------------------------------------------------------------------

/** Postgres error codes that will never succeed on retry. */
const PERMANENT_CODES = new Set([
  "23503", // foreign key violation — the goal is gone
  "23514", // check constraint — the payload is invalid
  "42501", // insufficient privilege — RLS said no
]);

const DUPLICATE_CODE = "23505"; // already inserted; a previous attempt did land

let flushing: Promise<number> | null = null;

/**
 * Push everything queued. Safe to call from anywhere, as often as you like:
 * concurrent calls share one run.
 *
 * @returns how many check-ins were accepted by the server during this run.
 */
export function flushOutbox(): Promise<number> {
  if (flushing) return flushing;

  flushing = (async () => {
    let delivered = 0;

    try {
      const pending = await listPending();
      if (pending.length === 0) return 0;

      // A queued check-in is worthless without a session to insert it under.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return 0;

      for (const entry of pending) {
        if (entry.permanent) continue;

        try {
          let photoPath: string | null = null;

          if (entry.photo) {
            const extension = extensionFor(entry.photo.type);
            photoPath = `${entry.owner_id}/${entry.id}.${extension}`;
            const { error: uploadError } = await supabase.storage
              .from(PROOF_PHOTOS_BUCKET)
              .upload(photoPath, entry.photo, {
                contentType: entry.photo.type || "image/jpeg",
                upsert: true, // a retry after a half-finished upload must not fail
              });

            if (uploadError) throw uploadError;
          }

          const { error } = await supabase.from("check_ins").insert({
            id: entry.id,
            goal_id: entry.goal_id,
            owner_id: entry.owner_id,
            note: entry.note,
            photo_path: photoPath,
            checked_in_at: entry.checked_in_at,
          });

          if (error && error.code !== DUPLICATE_CODE) throw error;

          await discardPending(entry.id);
          delivered += 1;
        } catch (error) {
          const code = (error as { code?: string })?.code;
          const message = errorMessage(error, "Could not sync this check-in");

          await update({
            ...entry,
            attempts: entry.attempts + 1,
            last_error: message,
            // Offline is not a failure, just a "not yet".
            permanent: code ? PERMANENT_CODES.has(code) : false,
          });

          // Network trouble: stop here and try the whole queue again later,
          // rather than burning through every entry against a dead connection.
          if (!navigator.onLine || isNetworkError(error)) break;
        }
      }
    } finally {
      flushing = null;
    }

    return delivered;
  })();

  return flushing;
}

function isNetworkError(error: unknown): boolean {
  const message = errorMessage(error).toLowerCase();
  return message.includes("failed to fetch") || message.includes("networkerror") ||
    message.includes("load failed");
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes("png")) return "png";
  if (mimeType.includes("webp")) return "webp";
  if (mimeType.includes("heic")) return "heic";
  return "jpg";
}

/**
 * Wire the queue up to every signal that connectivity might be back. Called
 * once, from the app root.
 */
export function startOutboxSync(onDelivered: (count: number) => void): () => void {
  const attempt = () => {
    void flushOutbox().then((count) => {
      if (count > 0) onDelivered(count);
    });
  };

  attempt();

  const onVisible = () => {
    if (document.visibilityState === "visible") attempt();
  };

  window.addEventListener("online", attempt);
  document.addEventListener("visibilitychange", onVisible);

  // The service worker pings us when a Background Sync fires (Android/desktop).
  const onMessage = (event: MessageEvent) => {
    if (event.data?.type === "flush-outbox") attempt();
  };
  navigator.serviceWorker?.addEventListener("message", onMessage);

  // Backstop for the case where the app sits open with no events at all.
  const interval = window.setInterval(attempt, 60_000);

  return () => {
    window.removeEventListener("online", attempt);
    document.removeEventListener("visibilitychange", onVisible);
    navigator.serviceWorker?.removeEventListener("message", onMessage);
    window.clearInterval(interval);
  };
}
